// Appearance: dark/light, defaulting to the OS preference and overridable per
// device from settings → Appearance ("Applies on this device only").
//
// The resolved theme lives on <html data-theme="light|dark">. A matching
// inline script in Components::Shell::Document (THEME_BOOT) sets it before
// first paint so there's no flash; this module keeps it in sync afterwards —
// OS preference changes, other tabs (storage events), and the <theme-picker>
// control. Markup convention (rendered by Components::Ui::ThemePicker):
//
//   <theme-picker>
//     <div role="radiogroup">
//       <button data-theme-option="system" aria-checked="…">…</button>
//       <button data-theme-option="light" …></button>
//       <button data-theme-option="dark" …></button>
//     </div>
//   </theme-picker>
//
// Active styling hangs entirely off aria-checked (aria-checked: /
// group-aria-checked: Tailwind variants), which this module owns.
//
// Progressive enhancement: without JS the THEME_BOOT script also hasn't run,
// so <html> has no data-theme and the prefers-color-scheme fallback block in
// application.css takes over. The picker simply does nothing.

type ThemePreference = "system" | "light" | "dark";

const STORAGE_KEY = "theme";
const media = window.matchMedia("(prefers-color-scheme: light)");

// What the user chose ("system" when unset) — not what's rendered.
const preference = (): ThemePreference => {
	let value: string | null = null;
	try {
		value = localStorage.getItem(STORAGE_KEY);
	} catch {
		// Storage unavailable (private mode etc.) — fall through to system.
	}
	return value === "light" || value === "dark" ? value : "system";
};

const resolve = (pref: ThemePreference): "light" | "dark" =>
	pref === "system" ? (media.matches ? "light" : "dark") : pref;

const apply = () => {
	document.documentElement.dataset.theme = resolve(preference());
	for (const picker of document.querySelectorAll("theme-picker")) {
		picker.sync();
	}
};

export const setPreference = (pref: ThemePreference) => {
	try {
		if (pref === "system") localStorage.removeItem(STORAGE_KEY);
		else localStorage.setItem(STORAGE_KEY, pref);
	} catch {
		// Can't persist — still apply for this page.
	}
	apply();
};

// Follow the OS while the preference is "system".
media.addEventListener("change", apply);

// Another tab changed the preference.
window.addEventListener("storage", (event) => {
	if (event.key === STORAGE_KEY) apply();
});

class ThemePicker extends HTMLElement {
	#abort: AbortController | null = null;

	connectedCallback() {
		this.#abort?.abort();
		this.#abort = new AbortController();

		this.addEventListener(
			"click",
			(event) => {
				const option = (event.target as Element | null)?.closest<HTMLElement>(
					"[data-theme-option]",
				);
				if (!option || !this.contains(option)) return;

				const value = option.dataset.themeOption;
				if (value === "system" || value === "light" || value === "dark") {
					this.#choose(value);
				}
			},
			{ signal: this.#abort.signal },
		);

		this.sync();
	}

	disconnectedCallback() {
		this.#abort?.abort();
	}

	// Reflect the stored preference onto aria-checked. Public because apply()
	// calls it on every picker when the preference changes elsewhere (another
	// tab, another picker instance).
	sync() {
		const current = preference();
		for (const option of this.querySelectorAll<HTMLElement>(
			"[data-theme-option]",
		)) {
			option.setAttribute(
				"aria-checked",
				String(option.dataset.themeOption === current),
			);
		}
	}

	#choose(value: ThemePreference) {
		setPreference(value); // apply() syncs every picker, including this one
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"theme-picker": ThemePicker;
	}
}

if (!customElements.get("theme-picker")) {
	customElements.define("theme-picker", ThemePicker);
}
